import {
  IconMenuHeaderSvg,
  ProfilePaymentSvg,
  ProfileLinkSvg,
  ProfilePrsnlSvg,
  ProfileReviewSvg,
  ProfileShippingSvg,
  ProfileStoreSvg,
} from 'components/_common/Icons';
import { FFL_ROLE_SLUG } from 'config/app.config';
import { calculateFormCompletion, normalizeFFLDetails } from 'hooks/useFormCompletion';
import { t } from 'i18next';
import PageHeader from 'modules/admin/components/_common/Header/PageHeader';
import ProfileCompletionBadge from 'modules/admin/components/subModules/AdminFFL/AdminFFLUpdateDetail/Sections/ProfileCompletionBadge';
import { useMemo } from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { adminProfileMain, adminProfileRoutes } from 'routes/admin/AdminRoutes';
import {
  userCurrentRoleSlugSelector,
  userFFLInfoSelector,
} from 'store/auth/selectors/authSelectors';
import { useAppSelector } from 'store/hooks';

type ProfileTabItem = {
  label: string;
  path: string;
  hasPermission: boolean;
  icon?: React.ReactNode;
};

export default function AdminProfileLayout() {
  const userRoleSlug = useAppSelector(userCurrentRoleSlugSelector);
  const fflInfo = useAppSelector(userFFLInfoSelector);
  const location = useLocation();

  const fflCompletion = useMemo(() => {
    if (userRoleSlug === FFL_ROLE_SLUG && fflInfo) {
      const normalized = normalizeFFLDetails(fflInfo);
      return calculateFormCompletion(normalized);
    }
    return null;
  }, [userRoleSlug, fflInfo]);

  const tabs: ProfileTabItem[] = useMemo(
    () => [
      {
        label: t('personal_information'),
        icon: <img src={ProfilePrsnlSvg} alt="personal information" />,
        hasPermission: true,
        path: adminProfileRoutes.profileGeneralInfo.path,
      },
      {
        label: t('store_settings'),
        icon: <img src={ProfileStoreSvg} alt="ffl information" />,
        hasPermission: userRoleSlug === FFL_ROLE_SLUG,
        path: adminProfileRoutes.profileFFLInfo.path,
      },
      {
        label: t('integration'),
        icon: <img src={ProfilePaymentSvg} alt="profile integration" />,
        hasPermission: userRoleSlug === FFL_ROLE_SLUG,
        path: adminProfileRoutes.profileIntergration.path,
      },
      {
        label: t('shippings'),
        icon: <img src={ProfileShippingSvg} alt="shipping configuration" />,
        hasPermission: userRoleSlug === FFL_ROLE_SLUG,
        path: adminProfileRoutes.profileShippingInfo.path,
      },
      {
        label: t('social_links'),
        icon: <img src={ProfileLinkSvg} alt="social links" />,
        hasPermission: userRoleSlug === FFL_ROLE_SLUG,
        path: adminProfileRoutes.profileSocialLinks.path,
      },
      {
        label: t('reviews_links'),
        icon: <img src={ProfileReviewSvg} alt="directory links" />,
        hasPermission: userRoleSlug === FFL_ROLE_SLUG,
        path: adminProfileRoutes.profileDirectoryLinks.path,
      },
      {
        label: t('free_shipping_rules'),
        icon: <img src={ProfileShippingSvg} alt="free shipping rules" />,
        hasPermission: userRoleSlug === FFL_ROLE_SLUG,
        path: adminProfileRoutes.profileFreeShippingRules.path,
      },
      {
        label: t('sidebar_menu_configuration'),
        icon: <img src={IconMenuHeaderSvg} alt="sidebar menu configuration" />,
        hasPermission: true,
        path: adminProfileRoutes.SidebarConfigPage.path,
      },
    ],
    [userRoleSlug]
  );

  const allowedTabs = useMemo(() => tabs.filter((tab) => tab.hasPermission), [tabs]);

  const activeTab = useMemo(
    () => allowedTabs.find((tab) => location.pathname.startsWith(tab.path)),
    [allowedTabs, location.pathname]
  );

  if (allowedTabs.length === 0) {
    return <div>You do not have permission to access this module.</div>;
  }

  if (!activeTab && location.pathname === adminProfileMain.path) {
    return <Navigate to={allowedTabs[0].path} replace />;
  }

  return (
    <div>
      <PageHeader
        title={t('profile')}
        showCount={false}
        rightSection={
          fflCompletion ? (
            <ProfileCompletionBadge
              staticPercentage={fflCompletion.percentage}
              staticSections={fflCompletion.sections}
            />
          ) : null
        }
      />

      <div className="module-tabs">
        {allowedTabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            className={({ isActive }) => (isActive ? 'tab active' : 'tab')}
          >
            {tab.icon && <span className="tab-icon">{tab.icon}</span>}
            {tab.label}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </div>
  );
}
